import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { AuthProvider } from './lib/auth';
import { LanguageProvider } from './lib/LanguageContext';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import Landing from './screens/Landing';
import SignIn from './screens/SignIn';
import SignUp from './screens/SignUp';
import ManualLogin from './screens/ManualLogin';
import ManualRegister from './screens/ManualRegister';
import ForgotPassword from './screens/ForgotPassword';
import ResetPassword from './screens/ResetPassword';
import BusinessSetup from './screens/BusinessSetup';
import CustomerIntake from './screens/CustomerIntake';
import CustomerHistory from './screens/CustomerHistory';
import CustomerDetail from './screens/CustomerDetail';
import LabelGeneration from './screens/LabelGeneration';
import RepairHistoryDetail from './screens/RepairHistoryDetail';
import DailyCash from './screens/DailyCash';
import BalanceHistory from './screens/BalanceHistory';
import RepairStatus from './screens/RepairStatus';
import PublicShop from './screens/PublicShop';
import AuthCallback from './screens/AuthCallback';
import PrivacyPolicy from './screens/PrivacyPolicy';
import TermsOfService from './screens/TermsOfService';
import Blog from './screens/Blog';
import BlogPost from './screens/BlogPost';
import BusinessEdit from './screens/BusinessEdit';
import Settings from './screens/Settings';
import AuthButton from './components/AuthButton';
import Footer from './components/Footer';
import { useAuth } from './lib/auth';
import { BusinessProfileProvider, useBusinessProfile } from './lib/businessProfileContext';
import { BalanceProvider } from './lib/balanceContext';
import { Menu } from 'lucide-react';
import { FloatingFeedbackButton } from './components/FloatingFeedbackButton';
import SEOHead from './components/SEOHead';
import JsonLd from './components/JsonLd';

function RequireBusinessProfile({ children }: { children: React.ReactNode }) {
  const { businessProfile, loading } = useBusinessProfile();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!businessProfile) {
    return <Navigate to="/setup" replace />;
  }

  return <>{children}</>;
}

/** Old /diagnostic/repair/:id and /completion/:tab/repair/:id links -> /history/repair/:id */
function RedirectToHistoryRepair() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/history/repair/${id}` : '/history'} replace />;
}

function AppLayout() {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  if (!user) {
    return (
      <>
        <SEOHead />
        <JsonLd type="landing" />
        <Landing />
      </>
    );
  }

  return (
    <BalanceProvider>
      <BusinessProfileProvider>
        <RequireBusinessProfile>
          <SEOHead noindex />
          <div className="flex min-h-screen bg-gray-100">
          <Sidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
          {/* min-w-0: wide children (e.g. horizontally scrolling rows) must not
              stretch the page beyond the viewport on phones */}
          <main className="flex-1 min-w-0 flex flex-col ml-0 lg:ml-64 pb-12 md:pb-14 lg:pb-0">
            {/* Mobile/Tablet Header with Hamburger */}
            <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-200 flex items-center justify-between px-3 py-2 md:p-4 h-12 md:h-14 shrink-0">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 text-gray-600 hover:text-gray-900"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <AuthButton />
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:flex sticky top-0 z-30 bg-white border-b border-gray-200 p-4 justify-end items-center h-14 shrink-0">
              <AuthButton />
            </div>

            <div className="p-2 md:p-3 lg:px-4 lg:py-3 flex-1">
            <Routes>
              <Route path="/" element={<CustomerIntake />} />
              <Route path="/history" element={<CustomerHistory />}>
                <Route index element={<></>} />
                <Route path="repair/:id" element={<RepairHistoryDetail />} />
              </Route>
              <Route path="/repair" element={<LabelGeneration />} />
              <Route path="/repair/:id" element={<LabelGeneration />} />
              <Route path="/customer/:id" element={<CustomerDetail />} />
              {/* Diagnostika / Yakunlash now live inside Mijoz tarixi; keep old links working */}
              <Route path="/diagnostic" element={<Navigate to="/history" replace />} />
              <Route path="/diagnostic/repair/:id" element={<RedirectToHistoryRepair />} />
              <Route path="/completion" element={<Navigate to="/history" replace />} />
              <Route path="/completion/:tab" element={<Navigate to="/history" replace />} />
              <Route path="/completion/:tab/repair/:id" element={<RedirectToHistoryRepair />} />
              <Route path="/daily-cash" element={<DailyCash />} />
              <Route path="/balance-history" element={<BalanceHistory />} />
              <Route path="/business/edit" element={<BusinessEdit />} />
                <Route path="/settings" element={<Settings />} />
            </Routes>
            </div>
            <Footer />
          </main>
          <BottomNav />
          <FloatingFeedbackButton />
        </div>
        </RequireBusinessProfile>
      </BusinessProfileProvider>
    </BalanceProvider>
  );
}

function App() {
  // Capture referral code from URL on any page load
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && ref.trim()) {
      localStorage.setItem('pendingReferralCode', ref.trim().toUpperCase());
      // Clean the ref param from URL without reload
      params.delete('ref');
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  return (
    <AuthProvider>
      <LanguageProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/status/:id" element={<><SEOHead noindex /><RepairStatus /></>} />
            <Route path="/shop/:id" element={<><SEOHead noindex /><PublicShop /></>} />
            <Route path="/auth/callback" element={<><SEOHead noindex /><AuthCallback /></>} />
            <Route path="/auth/signin" element={<><SEOHead /><SignIn /></>} />
            <Route path="/auth/signup" element={<><SEOHead /><SignUp /></>} />
            <Route path="/auth/forgot-password" element={<><SEOHead noindex /><ForgotPassword /></>} />
            <Route path="/auth/reset-password" element={<><SEOHead noindex /><ResetPassword /></>} />
            <Route path="/mlogin" element={<><SEOHead noindex /><ManualLogin /></>} />
            <Route path="/mregister" element={<><SEOHead noindex /><ManualRegister /></>} />
            <Route path="/setup" element={<><SEOHead noindex /><BusinessSetup /></>} />
            <Route path="/blog" element={<><SEOHead /><Blog /></>} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<><SEOHead /><PrivacyPolicy /></>} />
            <Route path="/terms" element={<><SEOHead /><TermsOfService /></>} />
            <Route path="/*" element={<AppLayout />} />
          </Routes>
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;